/**
 * Import Wizard Component
 * 
 * Main component that orchestrates the entire CSV import process
 * Integrates all sub-components and manages the multi-step workflow
 */

import React, { useState, useEffect, useCallback } from 'react';
import { X, HelpCircle } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import { useDropzone } from 'react-dropzone';

// Import wizard components
import FileUploader from './FileUploader';
import DataPreview from './DataPreview';
import ValidationResults from './ValidationResults';
import ImportProgress from './ImportProgress';
import ImportSummary from './ImportSummary';
import ImportHelp from './ImportHelp';

// Import utilities
import { parseFile } from './utils/csvParser';
import { autoMapColumns, getMappingConfidence, transformRowData } from './utils/dataMapper';
import { validateImportData, findPotentialDuplicates, PLACEHOLDER_VALUES } from './utils/validator';
import { recordImport, cleanupExpiredRollbacks } from './utils/importHistory';

// Constants for file validation
const SUPPORTED_FORMATS = {
  csv: ['.csv'],
  excel: ['.xlsx', '.xls'], 
  json: ['.json']
};

const SUPPORTED_MIME_TYPES = [
  'text/csv',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/json'
];

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const ImportWizard = ({ 
  isOpen, 
  onClose, 
  clients,
  selectedClient: initialClient,
  onImportComplete,
  supportedFormats = ['csv', 'xlsx', 'xls', 'json'],
  maxFileSize = MAX_FILE_SIZE
}) => {
  // Wizard state
  const [currentStep, setCurrentStep] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Step data
  const [selectedClient, setSelectedClient] = useState(initialClient || '');
  const [selectedFile, setSelectedFile] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [mappingResult, setMappingResult] = useState(null);
  const [mappingConfidence, setMappingConfidence] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [importProgress, setImportProgress] = useState({});
  const [importResult, setImportResult] = useState(null);

  // Cleanup expired rollbacks on component mount
  useEffect(() => {
    cleanupExpiredRollbacks();
  }, []);

  // Reset wizard when closed
  useEffect(() => {
    if (!isOpen) {
      resetWizard();
    }
  }, [isOpen]);

  const resetWizard = () => {
    setCurrentStep(1);
    setIsProcessing(false);
    setSelectedClient(initialClient || '');
    setSelectedFile(null);
    setParsedData(null);
    setMappingResult(null);
    setMappingConfidence(null);
    setValidationResult(null);
    setImportProgress({});
    setImportResult(null);
  };

  // File validation function
  const isValidFileType = useCallback((file) => {
    const extension = `.${file.name.split('.').pop().toLowerCase()}`;
    const allSupportedExtensions = Object.values(SUPPORTED_FORMATS).flat();
    
    // Check by file extension (primary method)
    if (allSupportedExtensions.includes(extension)) {
      return true;
    }
    
    // Check by MIME type (secondary method)
    if (SUPPORTED_MIME_TYPES.includes(file.type)) {
      return true;
    }
    
    // Check against provided supportedFormats prop
    const extensionWithoutDot = extension.substring(1);
    return supportedFormats.includes(extensionWithoutDot);
  }, [supportedFormats]);

  // File reader function
  const readFileContent = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (event) => {
        try {
          const extension = file.name.split('.').pop().toLowerCase();
          
          if (extension === 'json') {
            // Parse JSON file
            const content = JSON.parse(event.target.result);
            resolve(content);
          } else {
            // For CSV/Excel, return raw content (will be processed by parseFile later)
            resolve(event.target.result);
          }
        } catch (error) {
          reject(new Error(`Failed to parse ${extension.toUpperCase()} file: ${error.message}`));
        }
      };
      
      reader.onerror = () => reject(new Error('Failed to read file'));
      
      // Read as text for all file types (parseFile will handle CSV/Excel parsing)
      reader.readAsText(file);
    });
  };

  // Dropzone configuration
  const onDrop = useCallback(async (acceptedFiles) => {
    setError(null);
    setUploadProgress(0);
    
    if (acceptedFiles.length === 0) return;
    
    const file = acceptedFiles[0];
    
    // Validate file size
    if (file.size > maxFileSize) {
      setError('File size exceeds 10MB limit');
      return;
    }
    
    // Validate file type
    if (!isValidFileType(file)) {
      setError('Unsupported file type. Please use CSV, Excel (.xlsx, .xls), or JSON (.json) files');
      return;
    }
    
    // Determine file type for routing
    const extension = file.name.split('.').pop().toLowerCase();
    
    console.log(`Processing file: ${file.name}, extension: ${extension}, type: ${file.type}`);
    
    if (extension === 'json') {
      // JSON files: Use direct import flow
      console.log('Routing JSON file to direct import flow');
      setIsProcessing(true);
      
      try {
        // Read and parse JSON file content
        const content = await readFileContent(file);
        
        // Process the JSON import directly
        await onImportComplete(content, file);
        
        // Close the wizard on success
        onClose();
      } catch (error) {
        console.error('JSON import error:', error);
        setError(error.message || 'Failed to process JSON file');
      } finally {
        setIsProcessing(false);
        setUploadProgress(0);
      }
    } else {
      // CSV/Excel files: Route through the wizard flow
      console.log('Routing CSV/Excel file to wizard flow');
      await handleFileSelect(file);
    }
  }, [onImportComplete, onClose, isValidFileType, maxFileSize]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/json': ['.json']
    },
    multiple: false
  });

  const handleFileSelect = async (file) => {
    setSelectedFile(file);
    
    if (file) {
      try {
        setIsProcessing(true);
        const parsed = await parseFile(file);
        setParsedData(parsed);
        
        // Auto-map columns
        const mapping = autoMapColumns(parsed.headers);
        setMappingResult(mapping);
        
        // Calculate mapping confidence
        const confidence = getMappingConfidence(mapping);
        setMappingConfidence(confidence);
        
      } catch (error) {
        console.error('Error parsing file:', error);
        alert(`Error parsing file: ${error.message}`);
        setSelectedFile(null);
        setParsedData(null);
        setMappingResult(null);
        setMappingConfidence(null);
      } finally {
        setIsProcessing(false);
      }
    }
  };

  const handleMappingUpdate = (updatedMappingResult, updatedConfidence) => {
    setMappingResult(updatedMappingResult);
    setMappingConfidence(updatedConfidence);
  };

  const handleStepNext = async () => {
    if (currentStep === 1 && selectedFile && parsedData && mappingResult) {
      setCurrentStep(2);
    } else if (currentStep === 2) {
      // Proceed to validation
      setIsProcessing(true);
      try {
        // Get client information for enhanced processing
        const clientInfo = clients.find(c => c.id === selectedClient) || null;
        
        // Transform data using mapping with row indices and client info
        const transformedData = parsedData.data.map((row, index) => 
          transformRowData(row, mappingResult.mapping, index, clientInfo)
        );
        
        // Validate transformed data with client info
        const validation = validateImportData(transformedData, clientInfo);
        setValidationResult(validation);
        
        // Check for duplicates
        const duplicates = findPotentialDuplicates(transformedData);
        validation.duplicates = duplicates;
        
        setCurrentStep(3);
      } catch (error) {
        console.error('Error during validation:', error);
        alert(`Validation error: ${error.message}`);
      } finally {
        setIsProcessing(false);
      }
    } else if (currentStep === 3 && validationResult) {
      // Proceed to import
      await executeImport();
    }
  };

  const executeImport = async () => {
    setCurrentStep(4);
    setIsProcessing(true);
    
    const startTime = Date.now();
    const validRowsData = validationResult.validRowsData;
    const totalRows = validRowsData.length;
    let processedRows = 0;
    let successfulRows = 0;
    let failedRows = 0;
    const importedAssetIds = [];
    const importedAssets = [];
    const errors = [];
    
    try {
      // Get client name for display
      const client = clients.find(c => c.id === selectedClient);
      const clientName = client ? client.name : 'Unknown Client';
      
      // Get existing assets
      const existingAssets = JSON.parse(localStorage.getItem('crownJewelAssets') || '[]');
      
      // Process each valid row
      for (let i = 0; i < validRowsData.length; i++) {
        const rowData = validRowsData[i].data;
        const assetName = rowData.basicInfo?.name || 'Unknown Asset';
        
        // Update progress
        setImportProgress({
          currentRow: i + 1,
          totalRows,
          processedRows,
          successfulRows,
          failedRows,
          currentAssetName: assetName,
          status: 'processing',
          estimatedTimeRemaining: calculateETA(i, totalRows, startTime),
          processingRate: i > 0 ? (i / ((Date.now() - startTime) / 1000)) : 0
        });
        
        try {
          // Transform to flat structure for storage
          const flatAsset = transformToFlatAsset(rowData, selectedClient);
          flatAsset.id = uuidv4();
          flatAsset.createdAt = new Date().toISOString();
          flatAsset.lastUpdated = new Date().toISOString();
          flatAsset.importedAt = new Date().toISOString();
          flatAsset.importSource = selectedFile.name;
          
          // Add to storage
          existingAssets.push(flatAsset);
          importedAssetIds.push(flatAsset.id);
          importedAssets.push({
            name: flatAsset.assetName,
            ownerDepartment: flatAsset.ownerDepartment,
            criticalityClassification: flatAsset.criticalityClassification
          });
          
          successfulRows++;
        } catch (error) {
          console.error(`Error importing row ${i + 1}:`, error);
          errors.push({
            row: i + 1,
            message: error.message,
            assetName
          });
          failedRows++;
        }
        
        processedRows++;
        
        // Small delay to allow UI updates
        if (i % 10 === 0) {
          await new Promise(resolve => setTimeout(resolve, 10));
        }
      }
      
      // Save updated assets
      localStorage.setItem('crownJewelAssets', JSON.stringify(existingAssets));
      
      // Record import in history
      const importId = recordImport({
        clientId: selectedClient,
        clientName,
        fileName: selectedFile.name,
        fileType: selectedFile.type,
        totalRows,
        successfulRows,
        failedRows,
        importedAssetIds,
        errors,
        warnings: validationResult.warnings || []
      });
      
      // Create import result
      const endTime = Date.now();
      const processingTime = Math.round((endTime - startTime) / 1000);
      
      setImportResult({
        importId,
        timestamp: new Date().toISOString(),
        fileName: selectedFile.name,
        fileType: selectedFile.type,
        clientName,
        totalRows,
        successfulRows,
        failedRows,
        errors,
        warnings: validationResult.warnings || [],
        importedAssets,
        processingTime: `${processingTime}s`
      });
      
      // Update final progress
      setImportProgress({
        currentRow: totalRows,
        totalRows,
        processedRows,
        successfulRows,
        failedRows,
        currentAssetName: '',
        status: 'completed',
        estimatedTimeRemaining: 0,
        processingRate: totalRows / (processingTime || 1)
      });
      
      setCurrentStep(5);
      
      // Notify parent component
      if (onImportComplete) {
        onImportComplete({
          successful: successfulRows,
          failed: failedRows,
          total: totalRows
        });
      }
      
    } catch (error) {
      console.error('Import execution error:', error);
      setImportProgress(prev => ({
        ...prev,
        status: 'failed',
        error: error.message
      }));
    } finally {
      setIsProcessing(false);
    }
  };

  const transformToFlatAsset = (groupedData, clientId) => {
    return {
      clientId,
      // Basic Info
      assetName: groupedData.basicInfo?.name || '',
      assetDescription: groupedData.basicInfo?.description || '',
      assetLocation: groupedData.basicInfo?.location || '',
      // Owner Info - use email-safe placeholders for missing data
      ownerDepartment: groupedData.ownerInfo?.ownerDepartment || PLACEHOLDER_VALUES.TEXT,
      ownerName: groupedData.ownerInfo?.ownerName || PLACEHOLDER_VALUES.TEXT,
      ownerEmail: groupedData.ownerInfo?.ownerEmail || PLACEHOLDER_VALUES.EMAIL,
      // Security Info
      securityClassification: groupedData.securityInfo?.securityClassification || 'Internal Use',
      criticalityClassification: groupedData.securityInfo?.criticalityClassification || '4-Medium',
      boardInfo: groupedData.securityInfo?.boardInfo || 'No',
      executiveInfo: groupedData.securityInfo?.executiveInfo || 'No',
      financialInfo: groupedData.securityInfo?.financialInfo || 'No',
      otherConfidential: groupedData.securityInfo?.otherConfidential || 'No',
      // Data Info
      customerPII: groupedData.dataInfo?.customerPII || 'No',
      employeePII: groupedData.dataInfo?.employeePII || 'No',
      sensitivePII: groupedData.dataInfo?.sensitivePII || 'No',
      // Key Controls
      ssoImplemented: groupedData.keyControls?.ssoImplemented || 'No',
      rbacImplemented: groupedData.keyControls?.rbacImplemented || 'No',
      twoFactorAuth: groupedData.keyControls?.twoFactorAuth || 'No',
      networkSegmentation: groupedData.keyControls?.networkSegmentation || 'No',
      dataEncryption: groupedData.keyControls?.dataEncryption || 'No',
      userAccessReviews: groupedData.keyControls?.userAccessReviews || 'No',
      advancedEndpointProtection: groupedData.keyControls?.advancedEndpointProtection || 'No',
      onboardedOntoSOC: groupedData.keyControls?.onboardedOntoSOC || 'No',
      backupsImplemented: groupedData.keyControls?.backupsImplemented || 'No'
    };
  };

  const calculateETA = (current, total, startTime) => {
    if (current === 0) return null;
    const elapsed = (Date.now() - startTime) / 1000;
    const rate = current / elapsed;
    const remaining = total - current;
    return remaining / rate;
  };

  const handleStepBack = () => {
    if (currentStep > 1 && !isProcessing) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleCancelImport = () => {
    if (currentStep === 4 && isProcessing) {
      setImportProgress(prev => ({
        ...prev,
        status: 'cancelled'
      }));
      setIsProcessing(false);
    }
  };

  const handleViewAssets = () => {
    onClose();
    // This would trigger navigation to view assets
  };

  const handleStartNewImport = () => {
    resetWizard();
    setCurrentStep(1);
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return selectedClient && selectedFile && parsedData && mappingResult && !isProcessing;
      case 2:
        return mappingResult.mappedCount > 0 && !isProcessing;
      case 3:
        return validationResult && validationResult.validRows > 0 && !isProcessing;
      default:
        return false;
    }
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case 1: return 'Upload File';
      case 2: return 'Preview Data';
      case 3: return 'Validation Results';
      case 4: return 'Import Progress';
      case 5: return 'Import Complete';
      default: return 'Import';
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-blue-600 to-blue-700 flex-shrink-0">
            <div className="flex items-center space-x-4">
              <h2 className="text-xl font-semibold text-white">
                Crown Jewels Import - {getStepTitle()}
              </h2>
              <div className="flex items-center space-x-2">
                {[1, 2, 3, 4, 5].map((step) => (
                  <div
                    key={step}
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      step === currentStep
                        ? 'bg-white text-blue-600'
                        : step < currentStep
                        ? 'bg-blue-500 text-white'
                        : 'bg-blue-400 text-blue-100'
                    }`}
                  >
                    {step}
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowHelp(true)}
                className="text-white hover:text-blue-200 transition-colors"
                title="Help & Instructions"
              >
                <HelpCircle className="h-6 w-6" />
              </button>
              <button
                onClick={onClose}
                disabled={isProcessing}
                className="text-white hover:text-blue-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 p-6 overflow-y-auto min-h-0">
            {/* Error Display */}
            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">Import Error</h3>
                    <p className="text-sm text-red-700 mt-1">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {currentStep === 1 && (
              <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                  isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <input {...getInputProps()} />
                
                <div className="flex flex-col items-center space-y-2">
                  <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3-3m0 0l3 3m-3-3v12" />
                  </svg>
                  
                  <div className="text-gray-600">
                    Drop your CSV, Excel, or JSON file here or click to browse
                  </div>
                  
                  <div className="text-sm text-gray-500">
                    Supports CSV (.csv), Excel (.xlsx, .xls), and JSON (.json) files
                  </div>
                  
                  <div className="text-xs text-blue-600 mt-2">
                    💡 Need a CSV template? Download one from the main Crown Jewels page
                  </div>
                </div>
              </div>
            )}

            {currentStep === 2 && parsedData && mappingResult && mappingConfidence && (
              <DataPreview
                parsedData={parsedData}
                mappingResult={mappingResult}
                mappingConfidence={mappingConfidence}
                onMappingUpdate={handleMappingUpdate}
                onNext={handleStepNext}
                onBack={handleStepBack}
                disabled={isProcessing}
              />
            )}

            {currentStep === 3 && validationResult && (
              <ValidationResults
                validationResult={validationResult}
                onProceed={handleStepNext}
                onBack={handleStepBack}
                disabled={isProcessing}
              />
            )}

            {currentStep === 4 && (
              <ImportProgress
                progress={importProgress}
                onCancel={handleCancelImport}
                disabled={false}
              />
            )}

            {currentStep === 5 && importResult && (
              <ImportSummary
                importResult={importResult}
                onViewAssets={handleViewAssets}
                onStartNewImport={handleStartNewImport}
                onClose={onClose}
                disabled={false}
              />
            )}
          </div>

          {/* Footer - only show navigation for first 3 steps */}
          {currentStep <= 3 && (
            <div className="border-t p-6 bg-gray-50 flex-shrink-0">
              <div className="flex justify-between">
                <button
                  onClick={handleStepBack}
                  disabled={currentStep === 1 || isProcessing}
                  className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Back
                </button>
                <button
                  onClick={handleStepNext}
                  disabled={!canProceed()}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isProcessing ? 'Processing...' : currentStep === 3 ? 'Start Import' : 'Continue'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Help Modal */}
      <ImportHelp isOpen={showHelp} onClose={() => setShowHelp(false)} />
    </>
  );
};

export default ImportWizard; 